// DateSelector.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, CSSProperties } from "react";
import Button from "../../button/Button";
import "./DateSelector.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarLines, faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { MonthDropdown, YearDropdown } from "./MonthYearDropdown";
import { useTranslation } from "react-i18next";

interface IProps {
    date: Date;
    onChange: (date: Date) => void;
    closeOnPick?: boolean;
    minDate?: Date;
    maxDate?: Date;
    ariaLabel?: string;
}

const atStartOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const clampToRange = (d: Date, min?: Date, max?: Date) => {
    let res = d;
    if (min && res < atStartOfDay(min)) res = atStartOfDay(min);
    if (max && res > atStartOfDay(max)) res = atStartOfDay(max);
    return res;
};

function buildCalendarGrid(anchorMonth: Date) {
    const firstOfMonth = new Date(anchorMonth.getFullYear(), anchorMonth.getMonth(), 1);
    const startDay = firstOfMonth.getDay(); // 0 = Sun
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - startDay);

    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        cells.push(d);
    }
    return cells;
}

type DropDirection = "down" | "up";
const GAP = 8;
const VIEW_PAD = 8;

export default function DateSelector({
    date,
    onChange,
    closeOnPick = true,
    minDate,
    maxDate,
    ariaLabel = "Choose date",
}: IProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(atStartOfDay(date));

    const { t } = useTranslation();

    useEffect(() => {
        setViewDate(atStartOfDay(date));
    }, [date]);

    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
    const [direction, setDirection] = useState<DropDirection>("down");
    const [renderKey, setRenderKey] = useState(0); // remount panel to clear stale styles

    const monthLabel = useMemo(
        () => viewDate.toLocaleString(undefined, { month: "long", year: "numeric" }),
        [viewDate],
    );

    const daysShort = useMemo(() => {
        const base = new Date(2023, 0, 1); // Sunday
        return Array.from({ length: 7 }, (_, i) =>
            new Date(base.getFullYear(), base.getMonth(), base.getDate() + i).toLocaleDateString(undefined, {
                weekday: "short",
            }),
        );
    }, []);

    const gridDates = useMemo(() => buildCalendarGrid(viewDate), [viewDate]);

    const computePosition = () => {
        const trigger = triggerRef.current;
        const panel = panelRef.current;
        if (!trigger || !panel) return;

        const rect = trigger.getBoundingClientRect();

        const panelWidth = (panel as HTMLElement).scrollWidth || panel.offsetWidth;
        const panelHeight = (panel as HTMLElement).scrollHeight || panel.offsetHeight;

        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const spaceBelow = vh - rect.bottom - GAP;
        const spaceAbove = rect.top - GAP;
        const dropDown = spaceBelow >= panelHeight || spaceBelow >= spaceAbove;
        const nextDir: DropDirection = dropDown ? "down" : "up";

        const predictedLeft = rect.left;
        const predictedRight = predictedLeft + panelWidth;
        let shiftX = 0;
        if (predictedLeft < VIEW_PAD) shiftX = VIEW_PAD - predictedLeft;
        else if (predictedRight > vw - VIEW_PAD) shiftX = vw - VIEW_PAD - predictedRight;

        const maxH = Math.max(180, Math.floor(dropDown ? spaceBelow : spaceAbove));

        const style: CSSProperties = {
            position: "absolute",
            left: 0,
            transform: `translateX(${Math.round(shiftX)}px)`,
            zIndex: 999,
            ...(dropDown ? { top: `calc(100% + ${GAP}px)` } : { bottom: `calc(100% + ${GAP}px)` }),
            maxHeight: `${maxH}px`,
        };

        setDirection(nextDir);
        setPanelStyle(style);
    };

    useLayoutEffect(() => {
        if (!isOpen) return;

        const id1 = requestAnimationFrame(() => {
            computePosition();
            const id2 = requestAnimationFrame(() => computePosition());
            (computePosition as any)._raf2 = id2;
        });

        const onWin = () => computePosition();
        const ro = new ResizeObserver(() => computePosition());
        if (triggerRef.current) ro.observe(triggerRef.current);
        if (panelRef.current) ro.observe(panelRef.current);

        window.addEventListener("resize", onWin, { passive: true });
        window.addEventListener("scroll", onWin, { passive: true });

        return () => {
            cancelAnimationFrame(id1);
            if ((computePosition as any)._raf2) cancelAnimationFrame((computePosition as any)._raf2);
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Close on outside click
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!isOpen) return;
            const t = e.target as Node;
            if (triggerRef.current?.contains(t)) return; // let the trigger handle toggling
            if (!panelRef.current?.contains(t)) setIsOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [isOpen]);

    const formatDate = (d: Date): string => {
        const day = d.getDate().toString().padStart(2, "0");
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const year = d.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const goMonth = (delta: number) => {
        const next = new Date(viewDate);
        next.setMonth(viewDate.getMonth() + delta);
        setViewDate(next);
    };

    const withinBounds = (d: Date) => {
        const sd = atStartOfDay(d);
        if (minDate && sd < atStartOfDay(minDate)) return false;
        if (maxDate && sd > atStartOfDay(maxDate)) return false;
        return true;
    };

    const handlePick = (d: Date) => {
        const picked = clampToRange(atStartOfDay(d), minDate, maxDate);
        onChange(picked);
        if (closeOnPick) setIsOpen(false);
    };

    return (
        <div className="userful-date-selector-wrapper" data-direction={direction}>
            <div
                ref={triggerRef}
                className="userful-date-selector-component noselect"
                tabIndex={0}
                role="button"
                aria-label={ariaLabel}
                onClick={() => {
                    setIsOpen((o) => {
                        const next = !o;
                        if (next) setRenderKey((k) => k + 1);
                        return next;
                    });
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        (e.currentTarget as HTMLDivElement).click();
                    }
                }}
            >
                <p className="userful-date-selector-text">{formatDate(date)}</p>
                <FontAwesomeIcon className="userful-date-selector-icon" icon={faCalendarLines} />
            </div>

            {isOpen && (
                <div
                    key={renderKey}
                    ref={panelRef}
                    className="userful-date-panel"
                    style={panelStyle}
                    role="dialog"
                    aria-modal="false"
                >
                    <div className="userful-date-panel-header">
                        <Button variant="ghost" aria-label="Previous month" onClick={() => goMonth(-1)}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </Button>

                        <div className="userful-date-panel-month" aria-live="polite">
                            {monthLabel}
                        </div>

                        <Button variant="ghost" aria-label="Next month" onClick={() => goMonth(1)}>
                            <FontAwesomeIcon icon={faChevronRight} />
                        </Button>
                    </div>
                    <div className="userful-date-weekdays">
                        {daysShort.map((d) => (
                            <div key={d} className="userful-date-weekday" aria-hidden>
                                {d}
                            </div>
                        ))}
                    </div>

                    <div className="userful-date-grid" role="grid" aria-label="Calendar">
                        {gridDates.map((d) => {
                            const isOutMonth = d.getMonth() !== viewDate.getMonth();
                            const disabled = !withinBounds(d);
                            const isSelected = isSameDay(d, date);
                            const today = isSameDay(d, new Date());

                            return (
                                <button
                                    key={d.toISOString()}
                                    className={[
                                        "userful-date-cell",
                                        isOutMonth ? "out-month" : "",
                                        disabled ? "disabled" : "",
                                        isSelected ? "selected" : "",
                                        today ? "today" : "",
                                    ].join(" ")}
                                    role="gridcell"
                                    aria-selected={isSelected}
                                    disabled={disabled}
                                    onClick={() => handlePick(d)}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>

                    <div className="userful-date-panel-footer">
                        <Button variant="secondary" onClick={() => handlePick(new Date())}>
                            {t("Shared.today", "Today")}
                        </Button>
                        <div className="userful-date-panel-spacer" />
                        <Button variant="ghost" onClick={() => setIsOpen(false)}>
                            {t("Shared.close", "Close")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
